import React, { useState, useRef, useEffect } from "react";
import CommonTable from "../../components/commonComponent/CommanTable";
import { FaEye, FaEllipsisV, FaEdit, FaTrashAlt, FaFilter } from "react-icons/fa";
import CommanFilters from "../../components/commonComponent/CommanFilters";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "../../utils/axiosInstance";
import { toastError } from "../../utils/alertHelper";
import { confirmDelete } from "../../utils/alertHelper";
import { hasPermission } from "../../utils/permissionHelper";

const allFields = [
  { key: "srNo", label: "Sr.No." },
  { key: "orderid", label: "Order ID" },
  { key: "orderdate", label: "order Date" },
  { key: "customerName", label: "Customer Name" },
  { key: "customerNumber", label: "Customer Number" },
  { key: "userCategory", label: "User Category" },
  { key: "state", label: "State" },
  { key: "totalItem", label: "Total Item" },
  { key: "totalPrice", label: "Total Price" },
  { key: "status", label: "Status" },
];

const forcedFields = ["srNo"];

const CATEGORY_STYLES = {
  Bronze: { bg: '#E0E0E0', color: '#000' },
  Silver: { bg: '#E0E0E0', color: '#000' },
  Gold: { bg: '#FFE082', color: '#8D6E00' },
  Platinum: { bg: '#CFD8DC', color: '#000' },
};
const STATUS_STYLES = {
  New: { bg: '#DFE8FC', color: '#2563EB' },
  "On Hold": { bg: '#FEF1DB', color: '#F59E0B' },
  Confirmed: { bg: '#DCF1E4', color: '#16A34A' },
  "Partially Dispatched": { bg: '#FCE6DB', color: '#EA580C' },
  "Fully Dispatched": { bg: '#DAF0E9', color: '#059669' },
};
const getLabel = (v) => {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string' || typeof v === 'number') return String(v);
  if (typeof v === 'object') return v.name || v.label || v._id || '';
  return String(v);
};

function OrderIndex() {
  const [visibleFields, setVisibleFields] = useState(allFields.map(f => f.key));
  const [showColumnDropdown, setShowColumnDropdown] = useState(false);
  const columnDropdownRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState("");

  const [orders, setorders] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ startDate: "", endDate: "", userCategory: "", status: "" });
  const [filterDropdowns, setFilterDropdowns] = useState({ userCategories: [] });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const baseRoute = location && location.pathname && location.pathname.startsWith('/user') ? '/user' : (location && location.pathname && location.pathname.startsWith('/admin') ? '/admin' : '/admin');
  const [openMenuId, setOpenMenuId] = useState(null);

  // User permissions
  const [user, setUser] = useState(null);
  useEffect(() => {
    try {
      const stored = localStorage.getItem("user");
      if (stored) setUser(JSON.parse(stored));
    } catch { }
  }, []);

  // order permissions
  const canListorder = hasPermission(user, "Order Master", "List");
  const canVieworder = hasPermission(user, "Order Master", "View");
  const canCreateorder = hasPermission(user, "Order Master", "Create");
  const canEditorder = hasPermission(user, "Order Master", "Edit");
  const canDeleteorder = hasPermission(user, "Order Master", "Delete");
  const fetchorders = async () => {
    if (!canListorder && !canVieworder) {
      // still attempt fetch once for debugging; do not return early
    }
    try {
      console.debug("fetchorders: starting");
      setLoading(true);
      const page = 1;
      const limit = 1000; // Increase limit to get all orders
      const res = await axios.get(
        `/admin/api/order`
      );
      console.debug("fetchorders: response", res && res.data ? res.data : res);
      // Support multiple response shapes: `order`, `orders`, `data` etc.
      const payload = res && res.data ? res.data : {};
      const list =
        (Array.isArray(payload.order) && payload.order) ||
        (Array.isArray(payload.orders) && payload.orders) ||
        (Array.isArray(payload.data) && payload.data) ||
        [];
      setorders(list);
    } catch (err) {
      console.error("Failed to fetch orders:", err);
      const msg = (err && err.response && (err.response.data && (err.response.data.Message || err.response.data.message))) || err.message || String(err);
      toastError("Failed to fetch orders: " + msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchorders();
  }, [canListorder, canVieworder]);

  // load user categories for filter dropdown
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await axios.get('/admin/api/userCategory');
        const data = res?.data ?? res;
        let list = [];
        if (Array.isArray(data)) list = data;
        else if (Array.isArray(data.userCategories)) list = data.userCategories;
        else if (Array.isArray(data.data)) list = data.data;
        else if (Array.isArray(res?.data?.data)) list = res.data.data;
        if (!mounted) return;
        const opts = list.map(it => ({ value: it._id || it.id || it.value, label: it.name || it.title || it.label || it.categoryName }));
       
        const statusOpts = Object.keys(STATUS_STYLES).map(s => ({ value: s, label: s }));
        setFilterDropdowns(prev => ({ ...prev, userCategories: opts, statuses: statusOpts }));
      } catch (e) {
        
      }
    })();
    return () => { mounted = false; };
  }, []);

 
  const handleFilterChange = (k, v) => {
    setFilters(f => {
      const next = { ...f, [k]: v };
      if (k === 'userStatus') next.status = v;
      if (k === 'status') next.userStatus = v;
      return next;
    });
  };

  const filteredorders = orders.filter(
    (order) => {
      const q = searchTerm.toLowerCase();
      const code = (order.code || order.orderCode || "").toString().toLowerCase();
      const client = (
        (order.clientName && ((order.clientName.firstName || "") + " " + (order.clientName.lastName || ""))) ||
        (order.clientName && (order.clientName.firstName || "")) ||
        ""
      ).toLowerCase();
      let ok = code.includes(q) || client.includes(q) || (order.name || "").toLowerCase().includes(q);

      // apply date range filter
      if (ok && (filters.startDate || filters.endDate)) {
        const od = order.date || order.expectedDate || order.orderdate || null;
        const created = od ? new Date(od) : null;
        if (created) {
          if (filters.startDate) {
            const s = new Date(filters.startDate);
            if (created < s) return false;
          }
          if (filters.endDate) {
            const e = new Date(filters.endDate);
            e.setHours(23, 59, 59, 999);
            if (created > e) return false;
          }
        }
      }

      // apply user category filter
      if (ok && filters.userCategory) {
        const clientObj = order.clientName || {};
        const cat = clientObj.userCategory || clientObj.category || null;
        const catId = cat && (cat._id || cat.id || cat.value) ? (cat._id || cat.id || cat.value) : (typeof cat === 'string' ? cat : '');
        if (String(catId) !== String(filters.userCategory)) return false;
      }

      // apply status filter
      if (ok && filters.status) {
        const st = order.status || '';
        if (String(st).toLowerCase() !== String(filters.status).toLowerCase()) return false;
      }

      return ok;
    }
  );

  // Table columns
  const tableColumns = allFields
    .filter(
      (field) =>
        forcedFields.includes(field.key) || visibleFields.includes(field.key)
    )
    .map((field) => ({ key: field.key, label: field.label }))
    .concat([{ key: "actions", label: "Action" }]);

  // Delete handler
  const handleDelete = async (order) => {
    if (!canDeleteorder) {
      toastError("You do not have permission to delete orders.");
      return;
    }
    try {
      const res = await confirmDelete(`Are you sure you want to delete ${order.name || order.orderName}?`);
      if (!res.isConfirmed) return;
    } catch (err) {
      return;
    }
    try {
      await axios.delete(`/api/order/${order._id}`);
      // Refresh the list after deletion
      fetchorders();
    } catch (err) {
      console.error("Failed to delete order:", err);
      toastError("Failed to delete order. Please try again.");
    }
  };

  // Table data
  const tableData = filteredorders.map((order, idx) => {
    const client = order.clientName || {};
    const shipping = order.shipping || {};
    const orderDate = order.date || order.expectedDate || order.orderdate || "";
    return {
      ...order,
      srNo: idx + 1,
      orderid: order.code || order.orderCode || "",
      orderdate: orderDate ? new Date(orderDate).toLocaleDateString() : "",
      customerName: (client.firstName || "") + (client.lastName ? ` ${client.lastName}` : ""),
      customerNumber: client.mobileNumber || client.mobile || "",
      userCategory: (() => {
        const catRaw = client.userCategory || client.category || '';
        const cat = getLabel(catRaw) || '-';
        if (cat === '-') return '-';
        const style = CATEGORY_STYLES[cat] || { bg: '#EDEDED', color: '#000' };
        return (
          <span style={{ background: style.bg, color: style.color, padding: '4px 8px', borderRadius: 12, fontSize: '0.85rem' }}>
            {cat}
          </span>
        );
      })(),
      state: (shipping.state && (shipping.state.name || shipping.state)) || (client.bill && client.bill.state && (client.bill.state.name || client.bill.state)) || "",
      totalItem: Array.isArray(order.items) ? order.items.length : 0,
      totalPrice: order.grandAmount != null ? order.grandAmount : order.subTotal || "",
      status: (() => {
        const status = order.status || '';
        const style = STATUS_STYLES[status] || { bg: '#EDEDED', color: '#000' };
        return (
          <span style={{ background: style.bg, color: style.color, padding: '4px 8px', borderRadius: 12, fontSize: '0.85rem' }}>
            {status}
          </span>
        );
      })(),
      actions: (() => {
        // If a client user is logged in, show explicit View + Edit buttons
        if (user && user.userType === 'Client') {
          return (
            <div className="d-flex" style={{ gap: 8, alignItems: 'center' }}>
              <a
                href="#"
                className="text-muted"
                title="View"
                onClick={(e) => { e.preventDefault(); navigate(`${baseRoute}/order/view/${order._id}`); }}
                aria-label="View order"
              >
                <FaEye size={16} />
              </a>
              <a
                href="#"
                className="text-muted"
                title="Edit"
                onClick={(e) => { e.preventDefault(); navigate(`${baseRoute}/order/edit/${order._id}`); }}
                aria-label="Edit order"
              >
                <FaEdit size={16} />
              </a>
            </div>
          );
        }

        const anyAction = canVieworder || canEditorder || canDeleteorder;
        if (!anyAction) return null;

        // If only view permission, show a simple View button
        if (canVieworder && !canEditorder && !canDeleteorder) {
          return (
            <button
              className="btn btn-sm btn-outline-secondary"
              title="View"
              onClick={() => navigate(`${baseRoute}/order/view/${order._1}`)}
            >
              <FaEye size={14} />
            </button>
          );
        }

        // Otherwise show ellipsis menu with allowed actions
        return (
          <div className="d-flex align-items-center" style={{ position: "relative" }}>
            {(canEditorder || canDeleteorder) ? (
              <>
                <button
                  className="btn btn-sm shadow-none border-0 bg-transparent"
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenMenuId(
                      openMenuId === order._id ? null : order._id
                    );
                  }}
                >
                  <FaEllipsisV size={14} />
                </button>

                {openMenuId === order._id && (
                  <div
                    data-order-menu
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      position: "fixed",
                      zIndex: 1050,
                      background: "#fff",
                      border: "1px solid #ddd",
                      borderRadius: 6,
                      boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
                      minWidth: 150,
                      padding: "6px 0",
                    }}
                  >
                    {canVieworder && (
                      <button
                        className="btn btn-sm w-100 text-start border-0"
                        onClick={() => {
                          setOpenMenuId(null);
                          navigate(`${baseRoute}/order/view/${order._id}`);
                        }}
                      >
                        <FaEye className="me-2" /> View
                      </button>
                    )}

                    {canEditorder && (
                      <button
                        className="btn btn-sm w-100 text-start border-0"
                        onClick={() => {
                          setOpenMenuId(null);
                          navigate(`${baseRoute}/order/edit/${order._id}`);
                        }}
                      >
                        <FaEdit className="me-2" /> Edit
                      </button>
                    )}

                    {canDeleteorder && (
                      <button
                        className="btn btn-sm w-100 text-start border-0 text-danger"
                        onClick={() => {
                          setOpenMenuId(null);
                          handleDelete(order);
                        }}
                      >
                        <FaTrashAlt className="me-2" /> Delete
                      </button>
                    )}
                  </div>
                )}
              </>
            ) : (
              canVieworder && (
                <button
                  className="btn btn-sm shadow-none border-0 bg-transparent"
                  onClick={() =>
                    navigate(`${baseRoute}/order/view/${order._id}`)
                  }
                >
                  <FaEye />
                </button>
              )
            )}
          </div>
        );
      })(),
    };
  });



  useEffect(() => {
    if (!showColumnDropdown) return;
    const handleClickOutside = (event) => {
      if (
        columnDropdownRef.current &&
        !columnDropdownRef.current.contains(event.target)
      ) {
        setShowColumnDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, [showColumnDropdown]);

  // Close action menu when clicking outside (menu stops propagation)
  useEffect(() => {
    if (openMenuId == null) return;
    const handleDocClick = (e) => {
      try {
        // If the click occurred inside a menu, ignore it (menu handles its own clicks)
        if (e && e.target && e.target.closest && e.target.closest('[data-order-menu]')) return;
      } catch (err) {
        // ignore
      }
      setOpenMenuId(null);
    };
    document.addEventListener('mousedown', handleDocClick);
    return () => document.removeEventListener('mousedown', handleDocClick);
  }, [openMenuId]);

  return (
    <div className="container-fluid mt-4">
      <div className="d-flex justify-content-between align-orders-center mb-4 mt-4">
        <h2 className="fw-bold">Order</h2>
        <div className="d-flex gap-2 align-orders-center">
          <button className="btn btn-sm btn-light me-2" onClick={() => setShowFilters(s => !s)}>
            <FaFilter /> Filter
          </button>
          {!(user && user.userType === 'Client') && (
            <button
              className="btn btn-primary btn-sm"
              onClick={() => navigate(`${baseRoute}/order/create`)}
            >
              <i className="fas fa-plus me-1"></i> Add New order
            </button>
          )}

        </div>
      </div>
      {showFilters && (
        <div className="mb-3">
          <CommanFilters
            filters={filters}
            dropdowns={filterDropdowns}
            visibleFields={['dateRange', 'userCategory', 'status']}
            onFilterChange={handleFilterChange}
            onApplyFilters={() => { /* keep filter panel open; filters are applied via state */ }}
            onClearFilters={() => { setFilters({ startDate: "", endDate: "", userCategory: "", status: "", userStatus: "" }); /* keep panel open for further adjustments */ }}
          />
        </div>
      )}
      <div className="row mb-4">
        <div className="col-md-6"></div>
        <div className="col-md-6 d-flex justify-content-end">
          <div className="input-group" style={{ maxWidth: 280, borderRadius: "6px", overflow: "hidden" }}>
            <span className="input-group-text bg-white border-end-0" style={{ borderRadius: "6px 0 0 6px" }}>
              <i className="fas fa-search text-muted"></i>
            </span>
            <input
              type="text"
              className="form-control border-start-0"
              placeholder="Type to search..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              style={{ boxShadow: "none", borderRadius: "0 6px 6px 0" }}
            />
          </div>
        </div>
      </div>
      <CommonTable
        columns={tableColumns}
        data={tableData}
        ordersPerPageOptions={[10, 15, 25, 50]}
        defaultordersPerPage={15}
      />
    </div>
  );
}

export default OrderIndex;